"use client";

import { useCallback, useEffect, useState } from 'react';
import { dmsApi } from '@/lib/api';
import { formatBytes, formatUptime } from '@/lib/utils';
import {
  Server,
  HardDrive,
  Loader2,
  RefreshCw,
  Database,
  Mail,
  FileText,
  Users,
  Building2,
  Cloud,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';

interface SettingsPageProps {
  departmentId?: string;
  departmentName?: string;
}

interface SystemOverview {
  generatedAt?: string;
  scope?: { departmentId?: string | null; allDepartments?: boolean };
  server?: {
    status?: string;
    uptimeSeconds?: number;
    nodeEnv?: string;
  };
  storage?: {
    provider?: string;
    totalCapacityBytes?: number | null;
    uploadedBytes?: number;
    uploadedFiles?: number;
    dmsUsagePercent?: number | null;
    diskUsagePercent?: number | null;
    diskFreeBytes?: number | null;
    localPath?: string | null;
  };
  documents?: {
    files?: number;
    folders?: number;
    licenses?: number;
    indexStatus?: Record<string, number>;
  };
  organization?: {
    users?: number;
    departments?: number;
  };
  integrations?: {
    graphConfigured?: boolean;
    graphWebhookUrl?: string;
    emailIngestConfigured?: boolean;
    inboxSubscribed?: number;
    inboxSubscriptionErrors?: number;
    awsTextractConfigured?: boolean;
    licenseScanMock?: boolean;
  };
}

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Badge variant={ok ? 'default' : 'secondary'} className={ok ? 'bg-primary/10 text-primary hover:bg-primary/10' : ''}>
      {label}
    </Badge>
  );
}

export function SettingsPage({ departmentId, departmentName }: SettingsPageProps) {
  const [overview, setOverview] = useState<SystemOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadOverview = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    try {
      const data = await dmsApi.system.overview(departmentId);
      setOverview(data);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Could not load settings',
        description: err?.error ?? err?.message ?? 'Failed to fetch system overview.',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [departmentId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const storage = overview?.storage;
  const server = overview?.server;
  const docs = overview?.documents;
  const org = overview?.organization;
  const integrations = overview?.integrations;

  const usagePercent = storage?.dmsUsagePercent ?? 0;
  const diskPercent = storage?.diskUsagePercent ?? 0;
  const isOnline = server?.status === 'online';

  return (
    <div className="p-8 h-full overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-headline font-bold tracking-tight mb-1">Settings</h1>
            <p className="text-sm text-muted-foreground">
              Live server overview
              {departmentName ? ` for ${departmentName}` : overview?.scope?.allDepartments ? ' (all departments)' : ''}.
            </p>
            {overview?.generatedAt && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Updated {new Date(overview.generatedAt).toLocaleString()}
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => loadOverview(true)} disabled={isRefreshing}>
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Server className="h-5 w-5 text-primary" />
                    Storage server
                  </CardTitle>
                  <CardDescription>
                    Provider: <span className="font-medium text-foreground">{storage?.provider || '—'}</span>
                    {storage?.localPath ? ` · ${storage.localPath}` : ''}
                  </CardDescription>
                </div>
                <StatusBadge ok={isOnline} label={isOnline ? 'Online' : 'Degraded'} />
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Total capacity</span>
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-4 w-4 text-primary" />
                      <span className="text-lg font-semibold">
                        {storage?.totalCapacityBytes != null ? formatBytes(storage.totalCapacityBytes) : 'N/A'}
                      </span>
                    </div>
                    {storage?.diskFreeBytes != null && (
                      <span className="text-[11px] text-muted-foreground">
                        {formatBytes(storage.diskFreeBytes)} free on disk
                      </span>
                    )}
                  </div>
                  <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">Uploaded files</span>
                    <span className="text-lg font-semibold">{formatBytes(storage?.uploadedBytes)}</span>
                    <span className="text-[11px] text-muted-foreground">
                      {storage?.uploadedFiles ?? 0} file{(storage?.uploadedFiles ?? 0) === 1 ? '' : 's'} in DMS
                    </span>
                  </div>
                  <div className="rounded-xl border bg-muted/40 p-4 flex flex-col gap-1">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground">DMS usage</span>
                    <span className="text-lg font-semibold">
                      {storage?.dmsUsagePercent != null ? `${storage.dmsUsagePercent}%` : '—'}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {storage?.totalCapacityBytes
                        ? `${formatBytes(storage.uploadedBytes)} of ${formatBytes(storage.totalCapacityBytes)}`
                        : 'Of total disk capacity'}
                    </span>
                  </div>
                </div>

                {storage?.totalCapacityBytes != null && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>DMS files on disk</span>
                      <span>
                        {formatBytes(storage.uploadedBytes)} / {formatBytes(storage.totalCapacityBytes)}
                      </span>
                    </div>
                    <Progress value={usagePercent} className="h-2 rounded-full" />
                  </div>
                )}

                {storage?.diskUsagePercent != null && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Server disk usage</span>
                      <span>{diskPercent}%</span>
                    </div>
                    <Progress value={diskPercent} className="h-2 rounded-full" />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="h-4 w-4 text-primary" />
                    Documents
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" /> Files
                    </span>
                    <span className="font-medium">{docs?.files ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Folders</span>
                    <span className="font-medium">{docs?.folders ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents</span>
                    <span className="font-medium">{docs?.licenses ?? 0}</span>
                  </div>
                  {docs?.indexStatus && (
                    <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
                      <p className="font-medium text-foreground">Search index</p>
                      <p>Ready: {docs.indexStatus.ready ?? 0} · Pending: {docs.indexStatus.pending ?? 0}</p>
                      <p>Failed: {docs.indexStatus.failed ?? 0} · Skipped: {docs.indexStatus.skipped ?? 0}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Organization
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" /> Users
                    </span>
                    <span className="font-medium">{org?.users ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5" /> Departments
                    </span>
                    <span className="font-medium">{org?.departments ?? 0}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-muted-foreground">API uptime</span>
                    <span className="font-medium">{formatUptime(server?.uptimeSeconds)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Cloud className="h-4 w-4 text-primary" />
                  Integrations
                </CardTitle>
                <CardDescription>Email ingest, Microsoft Graph, and AWS Textract status.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge ok={!!integrations?.graphConfigured} label="Microsoft Graph" />
                  <StatusBadge ok={!!integrations?.emailIngestConfigured} label="Email ingest" />
                  <StatusBadge ok={!!integrations?.awsTextractConfigured && !integrations?.licenseScanMock} label="AWS Textract" />
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>
                      Inbox subscriptions: <strong className="text-foreground">{integrations?.inboxSubscribed ?? 0}</strong>
                      {(integrations?.inboxSubscriptionErrors ?? 0) > 0 && (
                        <span className="text-destructive ml-1">({integrations?.inboxSubscriptionErrors} errors)</span>
                      )}
                    </span>
                  </div>
                  {integrations?.graphWebhookUrl && (
                    <p className="text-xs text-muted-foreground truncate" title={integrations.graphWebhookUrl}>
                      Webhook: {integrations.graphWebhookUrl}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
